import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { FileImage } from '../../components/ui/ApiImage';
import {
  deleteAnchor,
  listAnchors,
  setAnchor,
  type AnchorInput,
  type AnchorShape,
  type LocationImage,
  type LocationNode,
} from './api';

interface Props {
  image: LocationImage;
  childLocations: LocationNode[];
  canEdit: boolean;
  onOpenChild: (id: string) => void;
  onClose: () => void;
}

interface Drag {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Full-size photograph with the markers that say where each child location is.
 *
 * Everything is stored as a fraction of the image rather than in pixels: the
 * picture is shown at whatever size the viewport allows, and on a phone that is
 * never the size it was marked up at.
 */
export function PhotoViewer({
  image,
  childLocations,
  canEdit,
  onOpenChild,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const surfaceRef = useRef<HTMLDivElement>(null);

  const [target, setTarget] = useState<string | null>(null);
  const [tool, setTool] = useState<AnchorShape>('rect');
  const [drag, setDrag] = useState<Drag | null>(null);

  const { data: anchors = [] } = useQuery({
    queryKey: ['anchors', image.file_id],
    queryFn: () => listAnchors(image.file_id),
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Escape backs out of the current placement first, and only closes the
      // viewer when there is nothing to back out of.
      if (target) setTarget(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [target, onClose]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['anchors', image.file_id] });

  const place = useMutation({
    mutationFn: (input: AnchorInput) => setAnchor(image.file_id, input),
    onSuccess: () => {
      setTarget(null);
      setDrag(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (anchorId: string) => deleteAnchor(anchorId),
    onSuccess: invalidate,
  });

  const anchored = new Set(anchors.map((anchor) => anchor.child_location_id));
  const unplaced = childLocations.filter((child) => !anchored.has(child.id));

  /** Pointer position as a fraction of the displayed image. */
  function fraction(event: React.PointerEvent) {
    const rect = surfaceRef.current!.getBoundingClientRect();
    return {
      x: Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1),
      y: Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1),
    };
  }

  function handlePointerDown(event: React.PointerEvent) {
    if (!target) return;
    const { x, y } = fraction(event);

    if (tool === 'point') {
      place.mutate({ child_location_id: target, shape: 'point', x, y });
      return;
    }
    // Capture keeps the drag alive when the pointer leaves the image, so a box
    // can be finished past the edge without the rectangle vanishing.
    (event.target as Element).setPointerCapture(event.pointerId);
    setDrag({ x, y, w: 0, h: 0 });
  }

  function handlePointerMove(event: React.PointerEvent) {
    if (!drag) return;
    const { x, y } = fraction(event);
    setDrag({ ...drag, w: x - drag.x, h: y - drag.y });
  }

  function handlePointerUp() {
    if (!drag || !target) return;
    // A stray click registers as a zero-size box; treat it as "nothing drawn"
    // rather than sending an area the server will refuse.
    if (Math.abs(drag.w) < 0.01 || Math.abs(drag.h) < 0.01) {
      setDrag(null);
      return;
    }
    place.mutate({
      child_location_id: target,
      shape: 'rect',
      x: drag.x,
      y: drag.y,
      w: drag.w,
      h: drag.h,
    });
  }

  const targetName = childLocations.find((child) => child.id === target)?.name ?? '';

  return (
    <div className="viewer" role="dialog" aria-modal="true">
      <div className="viewer__backdrop" onClick={onClose} />

      <div className="viewer__body">
        <header className="viewer__head">
          <strong>{image.caption ?? t('locations.photo')}</strong>
          <button type="button" onClick={onClose} aria-label={t('common.close')}>
            ×
          </button>
        </header>

        <div
          ref={surfaceRef}
          className={`viewer__surface${target ? ' viewer__surface--placing' : ''}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <FileImage
            fileId={image.file_id}
            alt={image.caption ?? t('locations.photo')}
          />

          {anchors.map((anchor) =>
            anchor.shape === 'rect' && anchor.w != null && anchor.h != null ? (
              <button
                key={anchor.id}
                type="button"
                className="viewer__area"
                style={{
                  left: `${anchor.x * 100}%`,
                  top: `${anchor.y * 100}%`,
                  width: `${anchor.w * 100}%`,
                  height: `${anchor.h * 100}%`,
                }}
                onClick={() => onOpenChild(anchor.child_location_id)}
              >
                <span>{anchor.label ?? anchor.child?.name}</span>
              </button>
            ) : (
              <button
                key={anchor.id}
                type="button"
                className="viewer__pin"
                style={{ left: `${anchor.x * 100}%`, top: `${anchor.y * 100}%` }}
                onClick={() => onOpenChild(anchor.child_location_id)}
              >
                <span className="viewer__pin-dot" />
                <span>{anchor.label ?? anchor.child?.name}</span>
              </button>
            ),
          )}

          {drag && (
            <div
              className="viewer__ghost"
              style={{
                left: `${Math.min(drag.x, drag.x + drag.w) * 100}%`,
                top: `${Math.min(drag.y, drag.y + drag.h) * 100}%`,
                width: `${Math.abs(drag.w) * 100}%`,
                height: `${Math.abs(drag.h) * 100}%`,
              }}
            />
          )}
        </div>

        {canEdit && (
          <div className="viewer__tools">
            {target ? (
              <p className="viewer__prompt">
                {tool === 'rect'
                  ? t('locations.dragToMark', { name: targetName })
                  : t('locations.clickToPlace', { name: targetName })}{' '}
                <button type="button" onClick={() => setTarget(null)}>
                  {t('common.cancel')}
                </button>
              </p>
            ) : (
              <>
                <div className="viewer__row">
                  <span className="viewer__hint">{t('locations.tool')}</span>
                  <label>
                    <input
                      type="radio"
                      checked={tool === 'rect'}
                      onChange={() => setTool('rect')}
                    />
                    {t('locations.toolArea')}
                  </label>
                  <label>
                    <input
                      type="radio"
                      checked={tool === 'point'}
                      onChange={() => setTool('point')}
                    />
                    {t('locations.toolPoint')}
                  </label>
                </div>

                {unplaced.length > 0 ? (
                  <div className="viewer__row">
                    <span className="viewer__hint">{t('locations.placeHint')}</span>
                    {unplaced.map((child) => (
                      <button key={child.id} type="button" onClick={() => setTarget(child.id)}>
                        {child.name}
                      </button>
                    ))}
                  </div>
                ) : childLocations.length > 0 ? (
                  <span className="viewer__hint">{t('locations.allPlaced')}</span>
                ) : (
                  <span className="viewer__hint">{t('locations.noChildrenToMark')}</span>
                )}
              </>
            )}

            {anchors.length > 0 && (
              <div className="viewer__row">
                {anchors.map((anchor) => (
                  <span key={anchor.id} className="viewer__chip">
                    {anchor.child?.name ?? anchor.child_location_id}
                    <button
                      type="button"
                      onClick={() => remove.mutate(anchor.id)}
                      aria-label={t('locations.removeAnchor')}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
